import ResponsiveImage from "../responsive-image/ResponsiveImage";
import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import "./ShopWithFilters.css";

import enTranslations from "./en.json";
import deTranslations from "./de.json";
import instrumentsData from "../../instrumente/instruments.js";

const ShopWithFilters = () => {
  const { t, i18n } = useTranslation();

  // Add translations for this component
  i18n.addResourceBundle("en", "shop", enTranslations, true, true);
  i18n.addResourceBundle("de", "shop", deTranslations, true, true);

  // Mapping von Typ-Codes zu vollständigen Namen
  const typeMapping = {
    g: "geige",
    b: "bratsche",
    c: "cello",
    k: "kontrabass",
  };

  // Placeholder-Images für die Entwicklung
  const getPlaceholderImage = (item) => {
    const title = i18n.language === "en" ? item.title_en : item.title_de;
    return `https://via.placeholder.com/400x600/8B4513/FFFFFF?text=${encodeURIComponent(
      title
    )}`;
  };

  // Shop items aus JSON-Daten erstellen
  const allShopItems = instrumentsData.map((item) => ({
    id: item.id,
    image: getPlaceholderImage(item), // Später durch echte Bilder ersetzen
    title: i18n.language === "en" ? item.title_en : item.title_de,
    description: i18n.language === "en" ? item.text_en : item.text_de,
    type: typeMapping[item.typ],
    year: item.jahr,
    images: item.bilder, // Array aller verfügbaren Bilder
  }));

  // Filter state
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [yearFilter, setYearFilter] = useState({
    operator: ">=",
    year: "1650",
  }); // Default to 1650
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTerms, setSearchTerms] = useState([]); // Array für mehrere Suchbegriffe

  // Available instrument types
  const instrumentTypes = [
    { key: "geige", label: t("shop:filter_violin", "Geige") },
    { key: "bratsche", label: t("shop:filter_viola", "Bratsche") },
    { key: "cello", label: t("shop:filter_cello", "Cello") },
    { key: "kontrabass", label: t("shop:filter_bass", "Kontrabass") },
  ];

  // Filter functions
  const filteredItems = useMemo(() => {
    return allShopItems.filter((item) => {
      // Type filter
      const typeMatch =
        selectedTypes.length === 0 || selectedTypes.includes(item.type);

      // Year filter
      let yearMatch = true;
      if (yearFilter.year !== "") {
        const filterYear = parseInt(yearFilter.year);
        if (yearFilter.operator === ">=") {
          yearMatch = item.year >= filterYear;
        } else {
          yearMatch = item.year <= filterYear;
        }
      }

      // Search filter - prüft sowohl die aktuellen Suchbegriffe als auch das aktive Suchfeld
      const searchMatch =
        (searchTerms.length === 0 && searchQuery === "") ||
        searchTerms.some(
          (term) =>
            item.title.toLowerCase().includes(term.toLowerCase()) ||
            item.description.toLowerCase().includes(term.toLowerCase())
        ) ||
        (searchQuery !== "" &&
          (item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.description
              .toLowerCase()
              .includes(searchQuery.toLowerCase())));

      return typeMatch && yearMatch && searchMatch;
    });
  }, [selectedTypes, yearFilter, searchQuery, searchTerms, allShopItems]);

  // Handler functions
  const toggleType = (type) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const addSearchTerm = () => {
    if (
      searchQuery.trim() !== "" &&
      !searchTerms.includes(searchQuery.trim())
    ) {
      setSearchTerms((prev) => [...prev, searchQuery.trim()]);
      setSearchQuery("");
    }
  };

  const removeSearchTerm = (term) => {
    setSearchTerms((prev) => prev.filter((t) => t !== term));
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addSearchTerm();
    }
  };

  const handleYearChange = (e) => {
    const value = e.target.value;

    // Allow temporary empty value during editing
    if (value === "") {
      setYearFilter((prev) => ({ ...prev, year: value }));
      return;
    }

    // Parse numeric input; allow temporary smaller numbers while typing
    const numericValue = parseInt(value);
    if (!isNaN(numericValue)) {
      setYearFilter((prev) => ({ ...prev, year: numericValue.toString() }));
    } else {
      // If not a valid number, don't update
      return;
    }
  };

  const handleYearBlur = (e) => {
    // If user leaves the field empty, set it back to default 1650
    if (e.target.value === "" || parseInt(e.target.value) < 1650) {
      setYearFilter((prev) => ({ ...prev, year: "1650" }));
    }
  };

  const handleTileClick = (item) => {
    // Navigate to detailed product page
    window.location.href =
      i18n.language === "en" ? `/en/product/${item.id}` : `/product/${item.id}`;
  };

  return (
    <div className="shop-with-filters">
      <div className="shop-content">
        {/* Main Title */}
        <div className="main-header">
          <h1 className="main-shop-title">{t("shop:title", "Unser Shop")}</h1>
        </div>

        {/* Non-instrumental Articles Button - moved out of filter section */}
        <div className="non-instrumental-btn-container">
          <button
            className="non-instrumental-btn focus-highlight"
            onClick={() =>
              window.open("https://geigenbau-jacobi.myshopify.com/", "_blank")
            }
            tabIndex={0}
            aria-label={t(
              "shop:non_instrumental_aria",
              "Nicht-instrumentale Artikel in neuem Tab öffnen"
            )}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                window.open(
                  "https://geigenbau-jacobi.myshopify.com/",
                  "_blank"
                );
              }
            }}
          >
            {t("shop:non_instrumental", "Non-instrumental Articles")}
          </button>
        </div>

        {/* Filter Section */}
        <div className="filter-section">
          {/* Main Filter Row */}
          <div className="filter-row">
            {/* Search */}
            <div className="filter-group">
              <label className="filter-label">
                {t("shop:search_label", "Suche")}
              </label>
              <div className="search-container">
                <input
                  type="text"
                  className="search-input"
                  placeholder={t(
                    "shop:search_placeholder",
                    "Suchbegriff eingeben und Enter drücken..."
                  )}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleSearchKeyPress}
                />
                {searchQuery.trim() !== "" && (
                  <button
                    className="add-search-btn"
                    onClick={addSearchTerm}
                    title={t("shop:add_search_term", "Suchbegriff hinzufügen")}
                  >
                    +
                  </button>
                )}
              </div>
            </div>

            {/* Instrument Type Filter */}
            <div className="filter-group">
              <label className="filter-label" id="instrument-type-label">
                {t("shop:type_filter_label", "Instrumententyp")}
              </label>
              <div
                className="type-filters"
                role="group"
                aria-labelledby="instrument-type-label"
              >
                {instrumentTypes.map((type) => (
                  <button
                    key={type.key}
                    className={`type-filter-btn focus-highlight ${
                      selectedTypes.includes(type.key) ? "active" : ""
                    }`}
                    onClick={() => toggleType(type.key)}
                    tabIndex={0}
                    role="button"
                    aria-pressed={selectedTypes.includes(type.key)}
                    aria-label={`${type.label} ${
                      selectedTypes.includes(type.key)
                        ? "ausgewählt"
                        : "nicht ausgewählt"
                    }`}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleType(type.key);
                      }
                    }}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Year Filter */}
            <div className="filter-group">
              <label className="filter-label">
                {t("shop:year_filter_label", "Baujahr")}
              </label>
              <div className="year-filter">
                <select
                  className="year-operator"
                  value={yearFilter.operator}
                  onChange={(e) =>
                    setYearFilter((prev) => ({
                      ...prev,
                      operator: e.target.value,
                    }))
                  }
                >
                  <option value=">=">{t("shop:year_after", "ab")}</option>
                  <option value="<=">{t("shop:year_before", "bis")}</option>
                </select>
                <input
                  type="number"
                  className="year-input"
                  placeholder="1650"
                  min="1650"
                  value={yearFilter.year}
                  onChange={handleYearChange}
                  onBlur={handleYearBlur}
                />
              </div>
            </div>
          </div>

          {/* Active Filters - nur Suchbegriffe anzeigen */}
          {(searchTerms.length > 0 || searchQuery !== "") && (
            <div className="active-filters filter-group standalone">
              <div className="active-filters-header">
                <span className="active-filters-label">
                  {t("shop:active_search_filters", "Aktive Suchfilter")}
                </span>
                <button
                  className="clear-search-btn"
                  onClick={() => {
                    setSearchTerms([]);
                    setSearchQuery("");
                  }}
                >
                  {t("shop:clear_search", "Suche zurücksetzen")}
                </button>
              </div>
              <div className="active-filter-tags">
                {searchTerms.map((term, index) => (
                  <div
                    key={`search-${index}`}
                    className="filter-tag search-tag"
                  >
                    "{term}"
                    <button
                      className="remove-filter-btn"
                      onClick={() => removeSearchTerm(term)}
                    >
                      ×
                    </button>
                  </div>
                ))}
                {searchQuery !== "" && (
                  <div className="filter-tag search-preview-tag">
                    "{searchQuery}" {t("shop:press_enter", "(Enter drücken)")}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Results Section */}
        <div className="results-section">
          <div className="results-header">
            <span className="results-count">
              {filteredItems.length} {t("shop:results", "Ergebnisse")}
            </span>
          </div>

          {filteredItems.length === 0 ? (
            <div className="no-results">
              <p>
                {t(
                  "shop:no_results",
                  "Keine Instrumente gefunden, die Ihren Filterkriterien entsprechen."
                )}
              </p>
            </div>
          ) : (
            <div
              className="shop-grid"
              role="grid"
              aria-label={t("shop:instruments_grid", "Instrumenten-Galerie")}
            >
              {filteredItems.map((item, index) => (
                <div
                  key={item.id}
                  className="shop-tile focus-highlight"
                  onClick={() => handleTileClick(item)}
                  tabIndex={0}
                  role="button"
                  aria-label={`${item.title} - ${item.description} - Details ansehen`}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleTileClick(item);
                    }
                  }}
                >
                  <div className="shop-tile-image-container">
                    <ResponsiveImage sizes="(max-width: 768px) 92vw, 30vw"
                      src={item.image}
                      alt={`${item.title} - Detailansicht eines hochwertigen Streichinstruments`}
                      className="shop-tile-image"
                    />
                  </div>
                  <div className="shop-tile-content">
                    <h3
                      className="shop-tile-title"
                      id={`tile-title-${item.id}`}
                    >
                      {item.title}
                    </h3>
                    <p
                      className="shop-tile-description"
                      aria-describedby={`tile-title-${item.id}`}
                    >
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShopWithFilters;
